import { Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Col, Row, Radio, Space } from "antd";

import { InputCheckboxGroup, InputHidden, InputRadioGroup } from "../../components/AppxForm";
import { InputInteger } from "../../components/ModelFormView";

import { GUTTER, COL_SIZE, COL_SIZE_PRICE } from "../../theme";
import { AuthContext } from "../../contexts/AuthContext";
import { moneyFormat } from "../../components/App/AppxUtils";

export default function DecoderPlans({
    minRecorders,
    formRef,
    onChange,
    currency,
}) {
    const { org, doModelAction } = useContext(AuthContext);
    const { t, i18n } = useTranslation('appxweb');
    const [ selectedDecoderOptions, setSelectedDecoderOptions ] = useState([]);
    const [ selectedRentalDecoder, setSelectedRentalDecoder ] = useState(null);
    const [ selectedPurchaseDecoder, setSelectedPurchaseDecoder ] = useState(null);
    const [ rentalDecoders, setRentalDecoders ] = useState([]);
    const [ purchaseDecoders, setPurchaseDecoders ] = useState([]);
    const [ decoderManagement, setDecoderManagement ] = useState(null);
    const onChangeRentalDecoderQty = (_, qty) => {
        let aux = null;

        if (selectedRentalDecoder?.id > 0) {
            aux = { ...selectedRentalDecoder };

            if (qty > 0) {
                aux.qty = qty;
                aux.amount = qty * aux.monthlyPrice;
            }
            else {
                aux.qty = 0;
                aux.amount = 0;
            }
        }

        setSelectedRentalDecoder({ ...aux });
        onChange("rentalDecoderAmount", aux?.amount||0);
    };
    const onChangePurchaseDecoderQty = (_, qty) => {
        let aux = null;

        if (selectedPurchaseDecoder?.id > 0) {
            aux = { ...selectedPurchaseDecoder };

            if (qty > 0) {
                aux.qty = qty;
                aux.amount = qty * aux.installPrice;
            }
            else {
                aux.qty = 0;
                aux.amount = 0;
            }
        }

        setSelectedPurchaseDecoder({ ...aux });
        onChange("purchaseDecoderAmount", aux?.amount||0);
    };

    useEffect(() => {
        if (decoderManagement) {
            let qty = 0;

            if (selectedPurchaseDecoder?.qty > 0) {
                qty += selectedPurchaseDecoder.qty;
            }

            if (selectedRentalDecoder?.qty > 0) {
                qty += selectedRentalDecoder.qty;
            }

            let amount = 0;

            if (qty > 0 && decoderManagement?.monthlyPrice > 0) {
                amount = parseFloat(decoderManagement.monthlyPrice) * qty;
            }

            setDecoderManagement({
                ...decoderManagement,
                qty: qty,
                amount: amount,
            });

            if (formRef?.current) {
                formRef.current.setFieldsValue({
                    decoderManagement: decoderManagement,
                    decoderManagementId: decoderManagement.id,
                    decoderManagementAmount: amount,
                    decoderManagementQty: qty,
                });
            }

            onChange("decoderManagementAmount", amount);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ decoderManagement?.id, selectedRentalDecoder?.qty, selectedPurchaseDecoder?.qty ]);

    useEffect(() => {
        doModelAction({
            model: "plan",
            action: "getdecoders",
        })
        .then(response => {
            let rd = [];
            let pd = [];

            if (Array.isArray(response?.data)) {
                for (let r of response.data) {
                    r.monthlyPrice = parseFloat(r.monthlyPrice) || 0;
                    r.installPrice = parseFloat(r.installPrice) || 0;

                    if (r.isEquipRental > 0) {
                        rd.push(r);
                    }
                    else {
                        pd.push(r);
                    }
                }
            }

            setRentalDecoders(rd);
            setPurchaseDecoders(pd);
        });
        doModelAction({
            model: "plan",
            action: "getdecodermanagement",
        })
        .then(response => {
            let dcm = null;

            if (response?.data) {
                dcm = response.data;
            }

            setDecoderManagement(dcm);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ org ]);

    if (rentalDecoders.length < 1 && purchaseDecoders.length < 1) {
        return null;
    }

    return (
        <Fragment>
        <Row gutter={GUTTER}>
            <Col {...COL_SIZE}>
                <div className="col-cnt col-cnt-left">
                    <strong style={{marginRight: 10}}>
                        {t('SignUpRequest.decoders')}:
                    </strong>
                    <InputCheckboxGroup
                        size="large"
                        name="decoder_options"
                        rules={[
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (minRecorders > 0) {
                                        if (!Array.isArray(value) || value.length < 1) {
                                            return Promise.reject(
                                                new Error(t('SignUpRequest.decoders_required'))
                                            );
                                        }

                                        const rentalDecoderId = getFieldValue("rentalDecoderId");
                                        const purchaseDecoderId = getFieldValue("purchaseDecoderId");

                                        if (!(rentalDecoderId > 0 || purchaseDecoderId > 0)) {
                                            return Promise.reject(
                                                new Error(t('SignUpRequest.decoders_required'))
                                            );
                                        }
                                    }

                                    return Promise.resolve(); // Success
                                },
                            }),
                        ]}
                        onChange={(v) => {
                            setSelectedDecoderOptions(v);

                            if (v.indexOf("LOCATION") < 0) {
                                setSelectedRentalDecoder(null);
                                formRef.current.setFieldsValue({
                                    rentalDecoderId: null,
                                    rentalDecoder: null,
                                    rentalDecoderQty: 0,
                                });
                                onChange("rentalDecoderAmount", 0);
                            }

                            if (v.indexOf("ACHAT") < 0) {
                                setSelectedPurchaseDecoder(null);
                                formRef.current.setFieldsValue({
                                    purchaseDecoderId: null,
                                    purchaseDecoder: null,
                                    purchaseDecoderQty: 0,
                                });
                                onChange("purchaseDecoderAmount", 0);
                            }
                        }}
                    >
                        {rentalDecoders.length > 0 && (
                        <div>
                            <Checkbox value="LOCATION">
                                {t('SignUpRequest.Location')}
                            </Checkbox>
                            <div style={{ padding: "16px 32px" }}>
                                <div>
                                    <InputHidden name="rentalDecoder" />
                                    <InputHidden name="rentalDecoderAmount" />
                                    <InputRadioGroup
                                        name="rentalDecoderId"
                                        disabled={(selectedDecoderOptions.indexOf("LOCATION") < 0)}
                                        onChange={(e) => {
                                            let o = null;

                                            for (let r of rentalDecoders) {
                                                if (r.id === e?.target?.value) {
                                                    o = r;
                                                    break;
                                                }
                                            }

                                            let qty = selectedRentalDecoder?.qty > 0
                                                ? selectedRentalDecoder.qty : 1;
                                            let amount = qty * o.monthlyPrice;

                                            setSelectedRentalDecoder({
                                                ...o,
                                                qty: qty,
                                                amount: amount,
                                            });

                                            if (formRef?.current) {
                                                formRef.current.setFieldsValue({
                                                    rentalDecoder: o,
                                                    rentalDecoderQty: qty,
                                                });
                                            }

                                            onChange("rentalDecoderAmount", amount);
                                        }}
                                        style={{ marginBottom: 0 }}
                                    >
                                        {rentalDecoders.map(r => (
                                        <div key={"rental-decoder-" + r.id}>
                                            <Radio value={r.id}>
                                                {t('SignUpRequest.rental_decoder_plan_des', {
                                                    label: i18n.language === "fr" ? r.labelFr : r.label,
                                                    amount: moneyFormat(r.monthlyPrice, currency),
                                                })}
                                            </Radio>
                                        </div>
                                        ))}
                                    </InputRadioGroup>
                                </div>
                                {selectedRentalDecoder?.canSetQty > 0 && (
                                <Space>
                                    <strong style={{marginRight: 15}}>
                                        {t('SignUpRequest.decoders_number')}:
                                    </strong>
                                    <InputInteger
                                        name="rentalDecoderQty"
                                        onChange={onChangeRentalDecoderQty}
                                        formRef={formRef}
                                        min={minRecorders}
                                        disabled={(selectedDecoderOptions.indexOf("LOCATION") < 0)}
                                        style={{
                                            display: 'inline-block',
                                            width: 140,
                                            margin: '13px 0 0',
                                        }}
                                    />
                                </Space>
                                )}
                            </div>
                        </div>
                        )}
                        {purchaseDecoders.length > 0 && (
                        <div>
                            <Checkbox value="ACHAT">
                                {t('SignUpRequest.Achat')}
                            </Checkbox>
                            <div style={{padding: "16px 32px" }}>
                                <div>
                                    <InputHidden name="purchaseDecoder" />
                                    <InputHidden name="purchaseDecoderAmount" />
                                    <InputRadioGroup
                                        name="purchaseDecoderId"
                                        disabled={(selectedDecoderOptions.indexOf("ACHAT") < 0)}
                                        onChange={(e) => {
                                            let o = null;

                                            for (let r of purchaseDecoders) {
                                                if (r.id === e?.target?.value) {
                                                    o = r;
                                                    break;
                                                }
                                            }

                                            let qty = selectedPurchaseDecoder?.qty > 0
                                                ? selectedPurchaseDecoder.qty : 1;
                                            let amount = qty * o.installPrice;

                                            setSelectedPurchaseDecoder({
                                                ...o,
                                                qty: qty,
                                                amount: amount,
                                            });

                                            if (formRef?.current) {
                                                formRef.current.setFieldsValue({
                                                    purchaseDecoder: o,
                                                    purchaseDecoderQty: qty,
                                                });
                                            }

                                            onChange("purchaseDecoderAmount", amount);
                                        }}
                                        style={{ marginBottom: 0 }}
                                    >
                                        {purchaseDecoders.map(r => (
                                        <div key={"purchase-decoder-" + r.id}>
                                            <Radio value={r.id}>
                                                {t('SignUpRequest.purchase_decoder_plan_des', {
                                                    label: i18n.language === "fr" ? r.labelFr : r.label,
                                                    amount: moneyFormat(r.installPrice, currency),
                                                })}
                                            </Radio>
                                        </div>
                                        ))}
                                    </InputRadioGroup>
                                </div>
                                {selectedPurchaseDecoder?.canSetQty > 0 && (
                                <Space>
                                    <strong style={{marginRight: 12}}>
                                        {t('SignUpRequest.decoders_number')}:
                                    </strong>
                                    <InputInteger
                                        name="purchaseDecoderQty"
                                        onChange={onChangePurchaseDecoderQty}
                                        formRef={formRef}
                                        min={minRecorders}
                                        disabled={(selectedDecoderOptions.indexOf("ACHAT") < 0)}
                                        style={{
                                            display: 'inline-block',
                                            width: 140,
                                            margin: '13px 0 0',
                                        }}
                                    />
                                </Space>
                                )}
                            </div>
                        </div>
                        )}
                    </InputCheckboxGroup>
                </div>
            </Col>
            <Col {...COL_SIZE_PRICE}>
                <div className="col-cnt col-cnt-right" style={{ marginBottom: 128 }}>
                    {selectedRentalDecoder && moneyFormat(selectedRentalDecoder.amount, currency)}
                </div>
                <div className="col-cnt col-cnt-right">
                    {selectedPurchaseDecoder && moneyFormat(selectedPurchaseDecoder.amount, currency)}
                </div>
            </Col>
        </Row>
        <InputHidden name="decoderManagement" />
        <InputHidden name="decoderManagementId" />
        <InputHidden name="decoderManagementQty" />
        <InputHidden name="decoderManagementAmount" />
        {decoderManagement?.qty > 0 && (
        <Row gutter={GUTTER}>
            <Col {...COL_SIZE}>
                <div className="col-cnt col-cnt-left">
                    {i18n?.language === "fr" ? decoderManagement.labelFr : decoderManagement.label}
                    <span style={{float: "right" }}>
                        {t('SignUpRequest.decoder_management_price', {
                            price: moneyFormat(decoderManagement.monthlyPrice, currency),
                        })}
                    </span>
                </div>
            </Col>
            <Col {...COL_SIZE_PRICE}>
                <div className="col-cnt col-cnt-right">
                    {moneyFormat(decoderManagement?.qty * decoderManagement.monthlyPrice, currency)}
                </div>
            </Col>
        </Row>
        )}
        </Fragment>
    );
}
